import React from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import moment from "moment";
// Reuse the Noto fonts already bundled for the quotation PDF so Japanese
// business names / addresses render correctly.
import NotoSansJPRegular from "../../../quotation/_components/pdf/fonts/NotoSansJP-Regular.ttf";
import NotoSansJPBold from "../../../quotation/_components/pdf/fonts/NotoSansJP-Bold.ttf";
import NotoSerifJPRegular from "../../../quotation/_components/pdf/fonts/NotoSerifJP-Regular.ttf";
import NotoSerifJPBold from "../../../quotation/_components/pdf/fonts/NotoSerifJP-Bold.ttf";

const tlf_logo_quotation =
  "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-quotation.png";

Font.register({
  family: "Noto Sans JP",
  fonts: [
    { src: NotoSansJPRegular, fontWeight: 400 },
    { src: NotoSansJPBold, fontWeight: 700 },
  ],
});

Font.register({
  family: "Noto Serif JP",
  fonts: [
    { src: NotoSerifJPRegular, fontWeight: 400 },
    { src: NotoSerifJPBold, fontWeight: 700 },
  ],
});

// Mirrors ITEM_TYPE_BADGES in InvoiceItemsDetail so the PDF reads with the
// same labels as the on-screen table.
const ITEM_TYPE_LABELS = {
  booking: "Booking",
  bookingeditfee: "Booking Edit Fee",
  additionalfee: "Additional Fee",
  cancelfee: "Cancel Fee",
};

const STATUS_LABELS = {
  PAID: "Paid",
  CHARGE_PENDING: "Charge Pending",
  UNPAID: "Unpaid",
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Noto Sans JP",
    fontSize: "12px",
    color: "#333",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 20,
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  headerTitle: {
    fontSize: "28px",
    fontFamily: "Noto Serif JP",
    fontWeight: 700,
    color: "#212529",
  },
  logo: {
    width: 50,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
    paddingBottom: 10,
  },
  clientInfo: {
    flex: 2,
    marginRight: 5,
  },
  companyInfo: {
    flex: 2,
    marginRight: 10,
  },
  quoteInfo: {
    flex: 1.5,
    alignItems: "flex-end",
  },
  infoLabel: {
    color: "#A7A7A7",
    fontSize: "8px",
    marginBottom: 5,
    textTransform: "uppercase",
  },
  infoContent: {
    fontSize: "10px",
    lineHeight: 1.5,
    paddingBottom: 10,
    color: "#4E4E4E",
  },
  infoName: {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    paddingBottom: 5,
    color: "#4E4E4E",
  },
  infoEmail: {
    color: "#BF993A",
    fontSize: "10px",
    paddingBottom: 10,
    textDecoration: "underline",
  },
  metaRow: {
    width: "100%",
    flexDirection: "row",
    fontSize: "11px",
  },
  metaLabel: {
    width: "50%",
    textAlign: "left",
    color: "#7C7B7B",
    fontSize: "10px",
    marginBottom: 7,
  },
  metaValue: {
    width: "50%",
    textAlign: "left",
    fontSize: "10px",
    lineHeight: 1.5,
    marginBottom: 7,
    color: "#4E4E4E",
  },
  totalSection: {
    flexDirection: "row",
    marginBottom: 30,
    borderWidth: 1,
    borderColor: "#ddd",
    width: 220,
  },
  periodBox: {
    width: "50%",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
  },
  periodLabel: {
    fontSize: "10px",
    textTransform: "uppercase",
    textAlign: "center",
    paddingTop: 10,
    color: "#666666",
  },
  periodValue: {
    fontSize: "10px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#282828",
    padding: 10,
  },
  totalAmount: {
    width: "50%",
  },
  totalLabel: {
    backgroundColor: "#282828",
    color: "white",
    padding: 10,
    textAlign: "center",
    fontSize: "10px",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  totalValue: {
    padding: 10,
    fontSize: "12px",
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "white",
    color: "#282828",
  },
  servicesSection: {
    marginBottom: 15,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#282828",
    color: "white",
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontWeight: "bold",
    fontSize: "9px",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#DADADA",
    paddingVertical: 8,
    paddingHorizontal: 10,
    color: "#4E4E4E",
    fontSize: "9px",
  },
  noCol: { width: "6%", textAlign: "left" },
  typeCol: { width: "17%", textAlign: "left" },
  refCol: { width: "12%", textAlign: "left" },
  dateCol: { width: "18%", textAlign: "left" },
  pickupCol: { width: "27%", textAlign: "left", paddingRight: 6 },
  amountCol: { width: "20%", textAlign: "right", flexShrink: 0 },
  pickupName: {
    fontSize: "9px",
    color: "#282828",
  },
  pickupAddress: {
    fontSize: "8px",
    color: "#8D8D8D",
  },
  emptyRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#DADADA",
    paddingVertical: 12,
    paddingHorizontal: 10,
    color: "#7C7B7B",
    fontSize: "9px",
    justifyContent: "center",
  },
  totalRow: {
    flexDirection: "row",
    backgroundColor: "#333333",
    color: "white",
    paddingVertical: 10,
    paddingHorizontal: 10,
    fontWeight: "bold",
    fontSize: "11px",
  },
  totalRowLabel: {
    width: "80%",
    textAlign: "right",
    paddingRight: 10,
  },
  totalRowValue: {
    width: "20%",
    textAlign: "right",
    flexShrink: 0,
  },
});

const periodText = (month, year) =>
  moment(`${year}-${String(month).padStart(2, "0")}-01`).locale("en").format("MMMM YYYY");

const getLocationDetail = (loc) => {
  const parts = loc?.formatted_address?.split(",");
  const name = parts?.[0] || "";
  const address = parts?.slice(1).join(",")?.trim() || "";
  return { name, address };
};

const InvoiceDocument = ({ invoice }) => {
  const items = invoice?.items || [];
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  const business = invoice?.user?.businessDetail?.businessInformation;
  const businessName = business?.businessName || invoice?.user?.name || "-";
  const businessPhone = business?.businessPhoneNumber
    ? `(${business?.businessPrefixNumber || business?.businessCountryCode || "-"}) ${
        business.businessPhoneNumber
      }`
    : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Invoice</Text>
          <View style={styles.logo}>
            <Image style={styles.image} src={tlf_logo_quotation} />
          </View>
        </View>

        {/* Info Section */}
        <View style={styles.infoSection}>
          <View style={styles.clientInfo}>
            <Text style={styles.infoLabel}>To</Text>
            <Text style={styles.infoName}>{businessName}</Text>
            <Text style={styles.infoEmail}>{invoice?.user?.email}</Text>
            {business?.businessAddress ? (
              <Text style={styles.infoContent}>{business.businessAddress}</Text>
            ) : null}
            {businessPhone ? <Text style={styles.infoContent}>{businessPhone}</Text> : null}
          </View>
          <View style={styles.companyInfo}>
            <Text style={styles.infoLabel}>From</Text>
            <Text style={styles.infoName}>THE LUXURY FLEET Co., Ltd.</Text>
            <Text style={styles.infoEmail}>info@theluxuryfleet.com</Text>
            <Text style={styles.infoContent}>
              {"Dai 2 Hayashi Building 1F/2F, \nKitashinagawa 1-8-20, Shinagawa-ku, \nTokyo 140-0001 \nJapan"}
            </Text>
            <Text style={styles.infoContent}>(+81) 3-6822-8772</Text>
          </View>
          <View style={styles.quoteInfo}>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Issue Date</Text>
              <Text style={styles.metaValue}>
                {moment(invoice?.date || invoice?.updatedDate).locale("en").format("MMMM D, YYYY")}
              </Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Status</Text>
              <Text style={styles.metaValue}>
                {STATUS_LABELS[String(invoice?.paymentStatus || "UNPAID").toUpperCase()] ||
                  invoice?.paymentStatus}
              </Text>
            </View>
          </View>
        </View>

        {/* Period / Total Section */}
        <View style={styles.totalSection}>
          <View style={styles.periodBox}>
            <Text style={styles.periodLabel}>Period</Text>
            <Text style={styles.periodValue}>{periodText(invoice?.month, invoice?.year)}</Text>
          </View>
          <View style={styles.totalAmount}>
            <Text style={styles.totalLabel}>Total</Text>
            <Text style={styles.totalValue}>{`${totalAmount.toLocaleString()} JPY`}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.servicesSection}>
          <View style={styles.tableHeader}>
            <Text style={styles.noCol}>#</Text>
            <Text style={styles.typeCol}>Type</Text>
            <Text style={styles.refCol}>Ref</Text>
            <Text style={styles.dateCol}>Travel Date</Text>
            <Text style={styles.pickupCol}>Pickup</Text>
            <Text style={styles.amountCol}>Amount</Text>
          </View>

          {items.length === 0 ? (
            <View style={styles.emptyRow}>
              <Text>No items in this invoice</Text>
            </View>
          ) : (
            items.map((item, index) => {
              const booking = item.booking;
              const pickup = getLocationDetail(booking?.pickupLocation);
              return (
                <View key={item._id || index} style={styles.tableRow} wrap={false}>
                  <Text style={styles.noCol}>{index + 1}</Text>
                  <Text style={styles.typeCol}>{ITEM_TYPE_LABELS[item.type] || item.type}</Text>
                  <Text style={styles.refCol}>
                    {booking?.key || booking?._id ? `#${booking.key || booking._id}` : "-"}
                  </Text>
                  <Text style={styles.dateCol}>
                    {booking?.pickupDateTime
                      ? moment(booking.pickupDateTime).format("YYYY/MM/DD hh:mm A")
                      : "-"}
                  </Text>
                  <View style={styles.pickupCol}>
                    <Text style={styles.pickupName}>{pickup.name || "-"}</Text>
                    {pickup.address ? (
                      <Text style={styles.pickupAddress}>{pickup.address}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.amountCol}>{`${(item.amount || 0).toLocaleString()} JPY`}</Text>
                </View>
              );
            })
          )}

          {/* Total Row */}
          <View style={styles.totalRow}>
            <Text style={styles.totalRowLabel}>Total</Text>
            <Text style={styles.totalRowValue}>{`${totalAmount.toLocaleString()} JPY`}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoiceDocument;
